/**
 * <TenantSwitcher /> — Task 16 / #49.
 *
 * Dropdown control in the app header that lets a user switch their active
 * tenant when they belong to two or more. Selecting a different tenant
 * PUTs to `/me/active-tenant` (which re-issues an idToken with the new
 * `custom:activeTenantId` claim), then calls `refreshSession()` to fold
 * the new claim back into AuthContext state, and finally navigates to `/`
 * so per-page caches are flushed against the new tenant.
 *
 * Renders nothing when:
 *   - The user has 0 or 1 memberships (shouldRenderSwitcher).
 *   - The user is currently emulating someone else — switching during an
 *     emulation would desync the actor vs. emulated-user tenants.
 *
 * User-facing strings are localized via react-i18next under the
 * `tenantSwitcher.*` keys in the `common` namespace (Task 18).
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useEmulation } from '../../contexts/EmulationContext'
import api from '../../services/api'
import {
  shouldRenderSwitcher,
  formatTenantLabel,
  clearTenantScopedStorage,
} from './tenantSwitcherHelpers'
import styles from './TenantSwitcher.module.scss'

export function TenantSwitcher() {
  const { t } = useTranslation('common')
  const { user, refreshSession } = useAuth()
  // EmulationContext is always mounted by Layout.jsx in production; the hook
  // throws when used outside the provider, matching the codebase pattern.
  const { isEmulating } = useEmulation()

  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const containerRef = useRef(null)

  // Close the dropdown on any outside click — matches the Navbar user menu
  // pattern (Navbar.jsx, lines 20–38).
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  if (!shouldRenderSwitcher(user?.memberships, isEmulating)) return null

  const memberships = user.memberships
  const activeTenantId = user.activeTenantId
  const activeMembership = memberships.find((m) => m.tenantId === activeTenantId)
  const activeName =
    activeMembership?.tenantName ||
    activeMembership?.tenantId ||
    t('tenantSwitcher.fallbackLabel')

  const handleSelect = async (tenantId) => {
    if (tenantId === activeTenantId) {
      setOpen(false)
      return
    }
    setSwitching(true)
    setErrorMessage(null)
    try {
      await api.put('/me/active-tenant', { tenantId })
      const result = await refreshSession()
      if (!result?.success) {
        // Surface to the user; the new claim should arrive on the next page
        // load even if this refresh failed, so we still navigate.
        console.error('refreshSession failed during tenant switch:', result?.error)
        setErrorMessage(t('tenantSwitcher.refreshFailed'))
      }
      // Clear any localStorage keys that hold tenant-scoped data, so the
      // newly-active tenant doesn't see stale IDs (e.g. import progress)
      // left over from the previous tenant.
      clearTenantScopedStorage()
      // Navigate to root with a full assign so all per-page caches (React
      // Query, Form.io, etc.) re-key against the new tenant context.
      window.location.assign('/')
    } catch (err) {
      console.error('Tenant switch failed:', err)
      setErrorMessage(
        err?.response?.data?.error ||
          err?.message ||
          t('tenantSwitcher.switchFailedFallback'),
      )
      setSwitching(false)
    }
  }

  return (
    <div className={styles.tenantSwitcher} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((prev) => !prev)}
        disabled={switching}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('tenantSwitcher.ariaSwitch')}
      >
        <span className={styles.prefix} aria-hidden="true">
          {t('tenantSwitcher.label')}
        </span>
        <span className={styles.triggerLabel}>
          {switching ? t('tenantSwitcher.switching') : activeName}
        </span>
        <span className={styles.caret} aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <ul className={styles.menu} role="listbox">
          {memberships.map((m) => {
            const isActive = m.tenantId === activeTenantId
            return (
              <li key={m.tenantId} className={styles.item}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`${styles.itemButton} ${isActive ? styles.active : ''}`}
                  onClick={() => handleSelect(m.tenantId)}
                  disabled={switching}
                  title={formatTenantLabel(m)}
                >
                  <span className={styles.tenantName}>{m.tenantName || m.tenantId}</span>
                  {m.role && <span className={styles.role}>{m.role}</span>}
                </button>
              </li>
            )
          })}
          {errorMessage && (
            <li className={styles.error} role="alert">{errorMessage}</li>
          )}
        </ul>
      )}
    </div>
  )
}

export default TenantSwitcher
